import { type FormType, type FormRegistry, getFormDefault, FORM_TTL_MS } from "~~/shared/types/session-forms";

const useSessionForm = <K extends FormType>(type: K) => {
  const nuxtApp = useNuxtApp();
  const { session, fetch: fetchSession } = useUserSession();

  const formKey = `${type}Form` as const;
  const savedAtKey = `${type}FormSavedAt` as const;
  const doneKey = `${type}FormDone` as const;
  const url = `/api/session/${type}` as const;

  const form = reactive<FormRegistry[K]>({
    ...getFormDefault(type),
  }) as FormRegistry[K];

  const save = async (): Promise<void> => {
    await $fetch(url as string, {
      method: "POST",
      body: { ...form },
      credentials: "include",
    });
    await nuxtApp.runWithContext(() => fetchSession());
  };

  const clear = async (): Promise<void> => {
    await $fetch(url as string, {
      method: "DELETE",
      credentials: "include",
    });
    await nuxtApp.runWithContext(() => fetchSession());
    Object.assign(form, getFormDefault(type));
  };

  const done = computed(() => !!session.value?.[doneKey]);

  const markDone = async (): Promise<void> => {
    await $fetch(url as string, {
      method: "PATCH",
      credentials: "include",
    });
    await nuxtApp.runWithContext(() => fetchSession());
  };

  let initialized = false;

  watch(
    () => session.value?.[formKey],
    (sessionData) => {
      if (!initialized) {
        initialized = true;
        const savedAt = session.value?.[savedAtKey];
        if (savedAt && Date.now() - savedAt > FORM_TTL_MS) {
          clear();
          return;
        }
      }
      Object.assign(form, getFormDefault(type), sessionData);
    },
    { immediate: true }
  );

  // Clear server session without resetting local form (avoids visual flash on leave)
  const clearServer = () => {
    $fetch(url as string, { method: "DELETE", credentials: "include" }).then(() =>
      nuxtApp.runWithContext(() => fetchSession())
    );
  };

  onBeforeRouteLeave((to) => {
    const targetName = String(to.name || "");
    if (targetName.startsWith(`${type}.`)) return;

    clearServer();
  });

  return { form, save, clear, done, markDone };
};

export const useSigninSession = () => useSessionForm("signin");
export const useRegisterSession = () => useSessionForm("register");
